import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../api/axios';
import { FileText, Edit, Receipt, Plus, Eye, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/dateFormat';
import { useOrganization } from '../../hooks/useOrganization';

const QuotationList = () => {
  const { isAdmin } = useAuth();
  const { hasPermission } = usePermissions();
  const { organization } = useOrganization();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusChangeModal, setStatusChangeModal] = useState({ show: false, quotation: null, newStatus: '' });

  useEffect(() => {
    fetchQuotations();
    fetchCustomers();
  }, [search, statusFilter, customerFilter, dateFrom, dateTo]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
    }
  };

  const fetchQuotations = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (customerFilter) params.customerId = customerFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      
      const [quotationsRes, invoicesRes] = await Promise.all([
        axios.get('/api/quotations', { params }),
        hasPermission('invoices.read') ? axios.get('/api/invoices') : Promise.resolve({ data: { data: [] } })
      ]);
      
      const quotationsData = quotationsRes.data.data;
      const invoicesData = invoicesRes.data?.data || [];
      
      // Add hasInvoice flag to each quotation
      const quotationsWithInvoiceFlag = quotationsData.map(quotation => ({
        ...quotation,
        hasInvoice: invoicesData.some(invoice => invoice.quotationId === quotation.id)
      }));
      
      setQuotations(quotationsWithInvoiceFlag);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Draft: 'bg-gray-100 text-gray-800',
      Sent: 'bg-blue-100 text-blue-800',
      Accepted: 'bg-green-100 text-green-800',
      Rejected: 'bg-red-100 text-red-800',
      Expired: 'bg-yellow-100 text-yellow-800'
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? quotations.map(q => q.id) : []);
  };

  const handleSelectItem = (id, checked) => {
    setSelectedItems(prev => 
      checked ? [...prev, id] : prev.filter(item => item !== id)
    );
  };

  const handleBatchDelete = async () => {
    if (!isAdmin || selectedItems.length === 0) return;
    if (!window.confirm(`Delete ${selectedItems.length} quotations?`)) return;
    
    try {
      await Promise.all(selectedItems.map(id => axios.delete(`/api/quotations/${id}`)));
      fetchQuotations();
      setSelectedItems([]);
    } catch (error) {
    }
  };

  const handleBatchPrint = () => {
    if (selectedItems.length === 0) return;
    const selectedQuotations = quotations.filter(q => selectedItems.includes(q.id));
    
    selectedQuotations.forEach(quotation => {
      window.open(`/accounts/quotations/${quotation.id}?print=true`, '_blank');
    });
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCustomerFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const handleStatusChange = (quotation) => {
    setStatusChangeModal({ show: true, quotation, newStatus: 'Sent' });
  };

  const confirmStatusChange = async () => {
    try {
      await axios.patch(`/api/quotations/${statusChangeModal.quotation.id}/status`, {
        status: statusChangeModal.newStatus
      });
      setStatusChangeModal({ show: false, quotation: null, newStatus: '' });
      fetchQuotations();
    } catch (error) {
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Quotations</h1>
        {hasPermission('quotations.create') && (
        <Link
          to="/accounts/quotations/create"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Quotation
        </Link>
        )}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <input
              type="text"
              placeholder="Search quotations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Expired">Expired</option>
            </select>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            <input
              type="date"
              placeholder="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="date"
              placeholder="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {isAdmin && (
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === quotations.length && quotations.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Quotation #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quotations.map((quotation) => (
                <tr key={quotation.id}>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(quotation.id)}
                        onChange={(e) => handleSelectItem(quotation.id, e.target.checked)}
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-gray-400 mr-2" />
                      {quotation.quotationNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {quotation.customer?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatDate(quotation.quotationDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatCurrency(quotation.total, organization?.currency_code)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(quotation.status)}>
                      {quotation.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/accounts/quotations/${quotation.id}`}
                        className="text-blue-600 hover:text-blue-900"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      {quotation.status !== 'Accepted' && hasPermission('quotations.update') && (
                        <Link
                          to={`/accounts/quotations/${quotation.id}/edit`}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          ✏️
                        </Link>
                      )}
                      {quotation.status === 'Draft' && (
                        <button
                          onClick={() => handleStatusChange(quotation)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Change Status"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {quotation.status === 'Accepted' && !quotation.hasInvoice && hasPermission('invoices.create') && (
                        <Link
                          to={`/accounts/invoices/create?quotationId=${quotation.id}`}
                          className="text-purple-600 hover:text-purple-900"
                          title="Create Invoice"
                        >
                          <Receipt className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {hasPermission('quotations.delete') && selectedItems.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-lg p-4 border">
          <div className="flex items-center gap-4">
            <span className="text-sm">{selectedItems.length} selected</span>
            <button
              onClick={handleBatchPrint}
              className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center gap-1"
            >
              <FileText className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleBatchDelete}
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {statusChangeModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Change Quotation Status</h3>
            <p className="text-gray-600 mb-4">
              Change status for quotation #{statusChangeModal.quotation?.quotationNumber}
            </p>
            <select
              value={statusChangeModal.newStatus}
              onChange={(e) => setStatusChangeModal(prev => ({ ...prev, newStatus: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
            >
              <option value="Sent">Sent</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Expired">Expired</option>
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStatusChangeModal({ show: false, quotation: null, newStatus: '' })}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationList;
